import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, RefreshCw, Sparkles, Copy } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { semanticApi, SearchAccountsResult, DuplicatesResult } from '@/lib/semanticSearch';

export function AccountSearchPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const [q, setQ] = useState(initialQ);
  const [result, setResult] = useState<SearchAccountsResult | null>(null);
  const [dups, setDups] = useState<DuplicatesResult | null>(null);

  function askAbout(code: string, label: string) {
    const question = t('account_search.ask_question', { code, label });
    navigate(`/app/workspace/${wsId}/qa?q=${encodeURIComponent(question)}`);
  }

  const search = useMutation({
    mutationFn: () => semanticApi.searchAccounts(wsId!, q.trim()),
    onSuccess: (r) => setResult(r),
  });
  const reindex = useMutation({
    mutationFn: () => semanticApi.reindexAccounts(wsId!),
  });
  const duplicates = useMutation({
    mutationFn: () => semanticApi.findDuplicates(wsId!),
    onSuccess: (r) => setDups(r),
  });

  function submit() {
    if (q.trim()) search.mutate();
  }

  // Auto-run when another page drills in via ?q=<account or query>.
  useEffect(() => {
    if (initialQ.trim() && wsId) search.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ, wsId]);

  return (
    <AppShell title={t('account_search.title')} subtitle={t('account_search.subtitle')}>
      <main className="mx-auto max-w-3xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('account_search.title')}</h1>
            <p className="mt-1 text-ink-600">{t('account_search.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => duplicates.mutate()}
              disabled={duplicates.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-ink-700 hover:bg-foreground/[0.04] text-xs disabled:opacity-50"
              title={t('account_search.duplicates_hint')}
            >
              <Copy className="h-3.5 w-3.5" />
              {duplicates.isPending ? t('account_search.finding') : t('account_search.find_duplicates')}
            </button>
            <button
              onClick={() => reindex.mutate()}
              disabled={reindex.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-ink-700 hover:bg-foreground/[0.04] text-xs disabled:opacity-50"
              title={t('account_search.reindex_hint')}
            >
              <RefreshCw className={'h-3.5 w-3.5 ' + (reindex.isPending ? 'animate-spin' : '')} />
              {reindex.isPending ? t('account_search.reindexing') : t('account_search.reindex')}
            </button>
          </div>
        </div>

        {reindex.data && (
          <p className="text-xs text-emerald-700">
            {reindex.data.mode === 'disabled'
              ? t('account_search.reindex_disabled')
              : t('account_search.reindexed', { count: reindex.data.indexed })}
          </p>
        )}

        <div className="rounded-md border border-border bg-white p-3 flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder={t('account_search.placeholder')}
            className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
          />
          <button
            onClick={submit}
            disabled={search.isPending || !q.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            <Search className="h-4 w-4" /> {t('account_search.search')}
          </button>
        </div>

        {result && (
          <div className="rounded-md border border-border bg-white">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <span className="text-xs text-muted-foreground">{t('account_search.results', { count: result.results.length })}</span>
              <span className="text-[10px] uppercase tracking-[0.14em] pill-emerald rounded px-1.5 py-0.5">
                {t(`account_search.mode_${result.mode}`)}
              </span>
            </div>
            {result.results.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">{t('account_search.no_results')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {result.results.map((m) => (
                  <li key={m.accountId} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <span className="num text-sm font-medium text-foreground">{m.code}</span>
                      <span className="text-sm text-ink-700 ms-2">{m.label}</span>
                      {m.category && <span className="text-[11px] text-muted-foreground ms-2">· {m.category}</span>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {m.score != null && (
                        <span className="num text-[11px] text-muted-foreground tabular-nums">{(m.score * 100).toFixed(0)}%</span>
                      )}
                      <button
                        onClick={() => askAbout(m.code, m.label)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800"
                      >
                        <Sparkles className="h-3.5 w-3.5" /> {t('account_search.ask_ai')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {dups && (
          <div className="rounded-md border border-border bg-white">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <span className="text-xs font-medium text-foreground">{t('account_search.duplicates_title')}</span>
              <span className="text-[10px] uppercase tracking-[0.14em] pill-emerald rounded px-1.5 py-0.5">
                {t(`account_search.mode_${dups.mode}`)}
              </span>
            </div>
            {dups.clusters.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">{t('account_search.no_duplicates')}</p>
            ) : (
              <>
                <p className="px-4 pt-3 text-xs text-muted-foreground">
                  {t('account_search.duplicates_found', { count: dups.clusters.length })}
                </p>
                <ul className="p-3 space-y-2">
                  {dups.clusters.map((cluster, i) => (
                    <li key={i} className="rounded-md border border-border/70 bg-foreground/[0.015] p-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {cluster.map((a) => (
                          <span
                            key={a.accountId}
                            className="inline-flex items-baseline rounded border border-border bg-white px-2 py-1 text-xs text-ink-700"
                            title={a.label}
                          >
                            <span className="num font-medium text-foreground">{a.code}</span>
                          </span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">{t('account_search.footnote')}</p>
      </main>
    </AppShell>
  );
}
